import { Localized } from "@fluent/react"
import { Button, Container, Dialog, List, ListItem, ListItemText } from "@mui/material"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { GenerateCodeHelpArticlesDialog } from "../../systemSettings/system/GenerateCodeHelpArticlesDialog"
import { SysMailFootersGenerateCodeDialog } from "../../system/mailing/sysFooters/SysMailFootersGenerateCodeDialog"
import { SysMailTemplatesGenerateCodeDialog } from "../../system/mailing/sysTemplates/SysMailTemplatesGenerateCodeDialog"
import { DefaultCompanySetting, loadDefaultCompanySettings } from "../../company/defaultCompanySettings/defaultCompanySettingsApi"
import { useAppDispatch } from "../../../app/hooks"
import { unwrapResult } from "@reduxjs/toolkit"
import { GenerateCodeDefaultCompanySettingsDialog } from "../../company/defaultCompanySettings/GenerateCodeDefaultCompanySettingsDialog"

type DialogState =
    | { type: "none" }
    | { type: "generateCodeDefaultCompanySettings", defaultCompanySettings?: DefaultCompanySetting[] }
    | { type: "generateCodeSysMailTemplates" }
    | { type: "generateCodeSysMailFooters" }
    | { type: "generateCodeHelpArticles" }

const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
    const onCancel = useCallback(() => {
        setter({ type: "none" })
    }, [setter])

    switch (state.type) {
        case "none":
            return null

        case "generateCodeDefaultCompanySettings":
            return <GenerateCodeDefaultCompanySettingsDialog
                companySettings={state.defaultCompanySettings}
                onCancel={onCancel}
            />

        case "generateCodeSysMailTemplates":
            return <SysMailTemplatesGenerateCodeDialog
                onCancel={onCancel}
            />

        case "generateCodeSysMailFooters":
            return <SysMailFootersGenerateCodeDialog
                onCancel={onCancel}
            />

        case "generateCodeHelpArticles":
            return <GenerateCodeHelpArticlesDialog
                onCancel={onCancel}
            />
    }
}

export const DataMigration = () => {
    const dispatch = useAppDispatch();
    
    const [defaultCompanySettings, setDefaultCompanySettings] = useState<DefaultCompanySetting[]>();
    const [dialog, setDialog] = useState<DialogState>({ type: "none" });

    useEffect(() => {
        dispatch(loadDefaultCompanySettings())
            .then(unwrapResult)
            .then(setDefaultCompanySettings)
            .catch((error) => console.error(error))
    }, [dispatch])

    const handleGenerateCodeDefaultCompanySettings = useCallback(() => {
        setDialog({ type: "generateCodeDefaultCompanySettings", defaultCompanySettings })
    }, [setDialog, defaultCompanySettings])

    const handleGenerateCode = useCallback((type: "generateCodeDefaultCompanySettings" | "generateCodeSysMailTemplates" | "generateCodeSysMailFooters" | "generateCodeHelpArticles") => {
        setDialog({ type })
    }, [setDialog])

    return (
        <>
            <Container maxWidth="sm">
                <List>
                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                        <ListItemText>
                            <Localized id="menu-default-company-settings">_Domyślne ustawienia firmowe</Localized>
                        </ListItemText>
                        <Button variant="outlined" color="secondary" onClick={() => handleGenerateCodeDefaultCompanySettings()}>
                            <Localized id="default-company-settings-generate-script">_Wygeneruj skrypt</Localized>
                        </Button>
                    </ListItem>

                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                        <ListItemText>
                            <Localized id="menu-system-mail-templates">_Systemowe szablony maili</Localized>
                        </ListItemText>
                        <Button variant="outlined" color="secondary" onClick={() => handleGenerateCode("generateCodeSysMailTemplates")}>
                            <Localized id="default-company-settings-generate-script">_Wygeneruj skrypt</Localized>
                        </Button>
                    </ListItem>

                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                        <ListItemText>
                            <Localized id="menu-system-mail-footers">_Systemowe szablony stopek</Localized>
                        </ListItemText>
                        <Button variant="outlined" color="secondary" onClick={() => handleGenerateCode("generateCodeSysMailFooters")}>
                            <Localized id="default-company-settings-generate-script">_Wygeneruj skrypt</Localized>
                        </Button>
                    </ListItem>

                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                        <ListItemText>
                            <Localized id="menu-system-help">_Ustawienia pomocy</Localized>
                        </ListItemText>
                        <Button variant="outlined" color="secondary" onClick={() => handleGenerateCode("generateCodeHelpArticles")}>
                            <Localized id="default-company-settings-generate-script">_Wygeneruj skrypt</Localized>
                        </Button>
                    </ListItem>
                </List>
            </Container>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth="xl" keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}